// @fileoverview : 경남-KPI > 품질불량률(O)
import { KPIActionHeader } from "@/features/kpi/actionHeader";
import { useKPIData } from "@/features/kpi/hook/useKPIData";
import { Header } from "@/features/standard/Header";
import { HeaderSubTitle } from "@/features/standard/SubTitle";
import { HeaderTitle } from "@/features/standard/Title";
import { GridTable } from "@/features/ui/PMS/gridTable";
import CustomTable from "@/features/ui/table/CustomTable";
import useColumnList from "@/hooks/useColumnList";
import { Audit } from "@/pages/KPI/leadtime";
import { KPIPageProps } from "@/pages/KPI/type";
import { DatetimeUtilMinute } from "@/utils/dateTimeUtil";
import { Flex } from "@mantine/core";
import {
  AuditKpiGet200ResponseRowsInner,
  KpiDefectRateGet200ResponseRowsInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const KPIDefect = Object.assign(
  {},
  {
    Header: Header,
    HeaderTitle: HeaderTitle,
    HeaderSubTitle: HeaderSubTitle,
    KPIActionHeader: KPIActionHeader,
    CustomTable: CustomTable,
  },
);

export const KPIDefectPage = ({ mode }: KPIPageProps) => {
  const queryClient = useQueryClient();
  const today = dayjs().subtract(1, "day").startOf("day").toDate();
  const oneWeekAgo = dayjs().subtract(7, "day").startOf("day").toDate();
  const columnlist = useColumnList();
  const { t } = useTranslation();

  const [content, setContent] = useState<any>([]);
  const [selectList, setSelectList] = useState<Set<number>>(new Set());
  const [stateDate, setStateDate] = useState<[Date | null, Date | null]>([oneWeekAgo, today]);
  const [item, setItem] = useState<string | null>("");
  const [totalQuantity, setTotalQuantity] = useState<number>(0);
  const [totalDefectQuantity, setTotalDefectQuantity] = useState<number>(0);
  const [totalDefectRate, setTotalDefectRate] = useState<string>("");

  const { itemData, defectData, auditKPIData } = useKPIData({
    mode,
    value: item,
    stateDate,
    options: Audit ? { fetchAuditKPI: true } : { fetchDefectRate: true },
  });

  useEffect(() => {
    const transform = (data: KpiDefectRateGet200ResponseRowsInner[]) =>
      data.map((d) => ({
        id: d.id,
        productionPlanId: d.productionPlanId,
        scheduledAt: d.scheduledAt,
        operationName: d.operationName,
        equipmentName: d.equipmentName,
        itemCode: d.itemCode,
        itemSpec: d.itemSpec,
        workerName: d.workerName,
        workEndTime: DatetimeUtilMinute(d.workEndTime ?? ""),
        workStartTime: DatetimeUtilMinute(d.workStartTime ?? ""),
        pauseDuration: d.pauseDuration,
        totalQuantity: `${d.totalQuantity} ${itemData?.unitText ?? ""}`,
        goodQuantity: `${d.goodQuantity} ${itemData?.unitText ?? ""}`,
        defectQuantity: `${d.defectQuantity} ${itemData?.unitText ?? ""}`,
        createdAt: d.createdAt,
        updatedAt: d.updatedAt,
        defectRate: d.defectRate,
      }));

    const totalQuantity = (data: KpiDefectRateGet200ResponseRowsInner[]) => {
      return data.reduce((sum, d) => sum + parseFloat(d.totalQuantity ?? ""), 0);
    };

    const totalDefectQuantity = (data: KpiDefectRateGet200ResponseRowsInner[]) => {
      return data.reduce((sum, d) => sum + parseFloat(d.defectQuantity ?? ""), 0);
    };

    const calculateAverage = (data: KpiDefectRateGet200ResponseRowsInner[]) => {
      if (!Array.isArray(data) || data.length === 0) return "0.00";
      const total = data.reduce((sum, d) => sum + parseFloat(d.defectRate ?? ""), 0);
      return (total / data.length).toFixed(2);
    };

    if (Array.isArray(defectData?.data)) {
      const transformed = transform(defectData?.data ?? []);
      setContent(transformed);
      setTotalQuantity(totalQuantity(transformed));
      setTotalDefectQuantity(totalDefectQuantity(transformed));
      setTotalDefectRate(calculateAverage(defectData?.data ?? []));
    } else if (Array.isArray(auditKPIData?.data?.rows ?? [])) {
      const rows =
        auditKPIData?.data.rows?.map((d: AuditKpiGet200ResponseRowsInner) => ({
          ...d,
          defectRate: d.defectRate !== undefined ? String(d.defectRate) : undefined,
        })) ?? [];
      const transformed = transform(rows);
      setContent(transformed);
      setTotalQuantity(totalQuantity(transformed));
      setTotalDefectQuantity(totalDefectQuantity(transformed));
      setTotalDefectRate(calculateAverage(rows));
    } else {
      setContent([]);
      setTotalDefectRate("0.00");
    }
  }, [defectData, auditKPIData, itemData]);

  const TableHeader = {
    machine: [
      t("item.code"),
      t("item.name"),
      t("item.spec"),
      t("kpi.totalQuantity"),
      t("kpi.defect.totalDefectQuantity"),
      t("kpi.defect.averageDefectRate"),
    ],
  };

  const BodyData = {
    machine: [
      {
        [t("item.code")]: itemData?.code ?? "-",
        [t("item.name")]: itemData?.name,
        [t("item.spec")]: itemData?.spec,
        [t("kpi.totalQuantity")]: totalQuantity,
        [t("kpi.defect.totalDefectQuantity")]: totalDefectQuantity,
        [t("kpi.defect.averageDefectRate")]: totalDefectRate === "NaN" ? "-" : totalDefectRate,
      },
    ],
  };

  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: ["kpi"] });
    };
  }, []);

  return (
    <Flex gap="md" direction={"column"} p="sm">
      <KPIDefect.Header>
        <KPIDefect.HeaderTitle>{t("kpi.defect.defect")}(Q)</KPIDefect.HeaderTitle>
        <KPIDefect.HeaderSubTitle>{t("kpi.defect.subTitle")}</KPIDefect.HeaderSubTitle>
      </KPIDefect.Header>
      <KPIDefect.KPIActionHeader
        setItem={setItem}
        stateDate={stateDate}
        setStateDate={setStateDate}
        mode={mode}
      />

      <KPIDefect.CustomTable headers={TableHeader.machine} data={BodyData.machine} />
      <GridTable
        headerList={columnlist.defectTable}
        row={content}
        setRow={setContent}
        selectList={selectList}
        //@ts-ignore
        setSelectList={setSelectList}
        width={"100%"}
        height={750}
        resizable={true}
      />
    </Flex>
  );
};
